import { useZoneContext } from 'context/ZoneContext';
import { useCallback, useContext } from 'react';
import { useControls } from 'react-zoom-pan-pinch';
import { getMapInitialScale, mapScales } from '../../../constants';
import { MapTourContext } from 'context/MapTourContext';
import { ExpandedStation, Station } from 'pages/Map/types';
import { getStation } from 'context/ZoneContext/utils';

export const useMapController = () => {
    const { stations, setExpandedStation, paths, activeStation, mapState, scale, position } = useZoneContext();
    const { isMapTour } = useContext(MapTourContext);
    const { zoomToElement, setTransform } = useControls();
    const { SEARCH_AUTO_ZOOM_MIN_SCALE, CURRENT_LOCATION_ZOOM_SCALE } = mapScales;

    const moveXPositionBy = useCallback(
        (x: number) => setTransform(position.x + x * scale, position.y, scale, 0),
        [scale, setTransform, position]
    );

    const zoomTo = useCallback(
        (id: string, isInitialScale = true) => {
            zoomToWithScale(id, isInitialScale ? getMapInitialScale(isMapTour) : scale);
        },
        [zoomToElement, scale]
    );

    const zoomToWithScale = useCallback(
        (id: string, scale: number) => {
            const element = document.getElementById(id);
            if (!element) return;
            zoomToElement(element, scale);
        },
        [zoomToElement]
    );

    const zoomToSearchEle = (activeSearchId: string) => {
        const station = getStation(stations, activeSearchId);
        if (station?.parentStation) {
            setExpandedStation(station.parentStation as ExpandedStation);
        } else {
            const path = paths.find(path => path.getId() === activeSearchId);
            const parentStation = path?.source.parentStation || path?.target.parentStation;
            parentStation && setExpandedStation(parentStation as ExpandedStation);
        }
        setTimeout(() => {
            zoomToWithScale(activeSearchId, Math.max(scale, SEARCH_AUTO_ZOOM_MIN_SCALE));
        }, 50);
    };

    const zoomToStation = (scrollToStation: Station, withZoom?: number) => {
        const parentStation = scrollToStation.parentStation;
        const zoomLevel = withZoom ?? scale;
        if (!parentStation) {
            zoomToWithScale(scrollToStation.id, zoomLevel);
            return;
        }
        setExpandedStation(parentStation as ExpandedStation);
        setTimeout(() => {
            zoomToWithScale(scrollToStation.id, zoomLevel);
        }, 0);
    };

    const zoomToSelectedStation = (station: Station) => {
        zoomToStation(station, Math.max(scale, mapScales.CURRENT_LOCATION_ZOOM_SCALE));
    };

    const zoomToCurrentLocation = () => {
        const scrollToStation = activeStation ?? mapState.current.lastInteractedStation ?? stations[0];
        zoomToStation(scrollToStation, Math.max(scale, CURRENT_LOCATION_ZOOM_SCALE));
    };

    return {
        moveXPositionBy,
        zoomTo,
        zoomToStation,
        zoomToSearchEle,
        zoomToWithScale,
        zoomToCurrentLocation,
        zoomToSelectedStation
    };
};
